class FcCartRecs extends HTMLElement {
  constructor() {
    super();
    if ('requestIdleCallback' in window) {
      requestIdleCallback(this.init.bind(this), { timeout: 1500 });
    } else {
      this.init();
    }
  }

  get slideContainer() {
    return this.querySelector('.swiper');
  }

  get popup() {
    if (!this._popup) {
      this._popup = document.getElementById(this.dataset.popupId);
    }
    return this._popup;
  }

  get popupBody() {
    return this.popup ? this.popup.querySelector('.fc-rec-popup__body') : null;
  }

  init() {
    this.fetchRecs(this.dataset.url).then((count) => {
      if (count < 2) {
        var fallbackUrl = this.dataset.url.replace('intent=complementary', 'intent=related');
        this.fetchRecs(fallbackUrl);
      }
    });
  }

  fetchRecs(url) {
    return fetch(url)
      .then((response) => response.text())
      .then((responseText) => {
        var sectionInnerHTML = new DOMParser()
          .parseFromString(responseText, 'text/html')
          .querySelector('.shopify-section');

        if (sectionInnerHTML === null) return 0;

        var recommendations = sectionInnerHTML.querySelector('fc-cart-recs');
        if (recommendations && recommendations.innerHTML.trim().length) {
          var products = recommendations.querySelectorAll('.fc-cart-rec-card');
          if (this.carousel && this.carousel.slider) {
            this.carousel.slider.destroy(true, true);
            this.carousel = null;
          }
          this.innerHTML = recommendations.innerHTML;
          this.initCarousel();
          this.bindEvents();
          if (products.length > 0) this.classList.remove('hidden');
          return products.length;
        }
        return 0;
      })
      .catch((error) => {
        console.error(error);
        return 0;
      });
  }

  initCarousel() {
    if (!this.slideContainer) return;
    var drawerBody = this.closest('.drawer__body');
    var offset = drawerBody ? Math.round(parseFloat(getComputedStyle(drawerBody).paddingLeft)) : 32;
    this.carousel = new FoxTheme.Carousel(this.slideContainer, {
      slidesPerView: 'auto',
      spaceBetween: 8,
      slidesOffsetBefore: offset,
      slidesOffsetAfter: offset,
      loop: false,
      breakpoints: {
        768: {
          spaceBetween: 12,
        },
      },
    });
    this.carousel && this.carousel.init();
  }

  bindEvents() {
    if (this._eventsBound) return;
    this._eventsBound = true;
    this.addEventListener('click', (event) => {
      const atcButton = event.target.closest('.fc-cart-rec-card__atc');
      if (atcButton) {
        event.preventDefault();
        this.onAtcClick(atcButton);
        return;
      }
    });
  }

  getCardData(card) {
    const script = card.querySelector('.fc-cart-rec-card__data');
    if (!script) return null;
    try {
      return JSON.parse(script.textContent);
    } catch (e) {
      return null;
    }
  }

  onAtcClick(button) {
    if (button.disabled || button.classList.contains('btn--loading')) return;

    const card = button.closest('.fc-cart-rec-card');
    if (!card) return;

    const data = this.getCardData(card);
    const variantId = button.dataset.variantId;

    if (data && data.hasSizes) {
      this.showSizePopup(card, data, variantId);
    } else {
      button.classList.add('btn--loading');
      this.addToCart(variantId);
    }
  }

  showSizePopup(card, data, currentVariantId) {
    if (!this.popup || !this.popupBody) return;

    const colorKey = 'option' + (data.colorIndex + 1);
    const sizeKey = 'option' + (data.sizeIndex + 1);

    // Scope the size list to the color shown on the card, derived from the
    // ATC button's variant id (the cards have no color swatches).
    const currentVariant = data.variants.find((v) => String(v.id) === String(currentVariantId));
    const currentColor = data.colorIndex >= 0 && currentVariant ? currentVariant[colorKey] : null;

    let sizeVariants;
    if (currentColor) {
      sizeVariants = data.variants.filter((v) => v[colorKey] === currentColor);
    } else {
      sizeVariants = data.variants;
    }

    const sizeLabel = data.sizeOptionName || 'Maat';
    const firstAvailable = sizeVariants.find((v) => v.available);

    const sizesHtml = sizeVariants.map((v) => {
      const sizeValue = v[sizeKey] || v.option1;
      const isActive = firstAvailable && v.id === firstAvailable.id;
      return '<button type="button" class="fc-rec-popup__size' + (isActive ? ' is-active' : '') + '"'
        + ' data-variant-id="' + v.id + '"'
        + (v.available ? '' : ' disabled')
        + '>' + sizeValue + '</button>';
    }).join('');

    const atcLabel = this.dataset.atcLabel || 'IN WINKELWAGEN';

    this.popupBody.innerHTML =
      '<p class="fc-rec-popup__title h5">Selecteer je ' + sizeLabel.toLowerCase() + '</p>'
      + '<div class="fc-rec-popup__sizes">' + sizesHtml + '</div>'
      + '<button type="button" class="fc-rec-popup__atc btn btn--primary w-full">'
      + '<span class="btn__text">' + atcLabel + '</span>'
      + '<div class="loading__spinner hidden">'
      + '<svg aria-hidden="true" focusable="false" class="spinner icon icon--extra-medium" viewBox="0 0 66 66" xmlns="http://www.w3.org/2000/svg">'
      + '<circle class="path" fill="none" stroke-width="4" cx="33" cy="33" r="30"></circle>'
      + '</svg></div></button>';

    if (this._popupHandler) {
      this.popupBody.removeEventListener('click', this._popupHandler);
    }
    this._popupHandler = (event) => {
      const sizeBtn = event.target.closest('.fc-rec-popup__size');
      if (sizeBtn && !sizeBtn.disabled) {
        this.popupBody.querySelectorAll('.fc-rec-popup__size').forEach((b) => b.classList.remove('is-active'));
        sizeBtn.classList.add('is-active');
        return;
      }

      const atcBtn = event.target.closest('.fc-rec-popup__atc');
      if (atcBtn) {
        const activeSize = this.popupBody.querySelector('.fc-rec-popup__size.is-active');
        if (!activeSize) return;
        atcBtn.classList.add('btn--loading');
        this.addToCart(activeSize.dataset.variantId);
      }
    };
    this.popupBody.addEventListener('click', this._popupHandler);

    this.popup.show();
  }

  addToCart(variantId) {
    return fetch(FoxTheme.routes.cart_add_url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: parseInt(variantId), quantity: 1 }),
    })
      .then((response) => response.json())
      .then((parsedState) => {
        if (parsedState.status) {
          console.error('FC Cart Recs: add to cart error', parsedState.description);
          return;
        }
        if (this.popup && typeof this.popup.hide === 'function') this.popup.hide();
        document.dispatchEvent(new CustomEvent('cart:refresh'));
      })
      .catch((error) => {
        console.error(error);
      });
  }
}

customElements.define('fc-cart-recs', FcCartRecs);
